import { AppRouting } from "AppRouting";
import { CovFeatureGate } from "@cx-component-library/react-components/feature-flags";
import { CssBaseline } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { CdcScriptStatus } from "components/cdc/CdcScriptStatus";
import { ErrorBoundary } from "components/errorBoundary/ErrorBoundary";
import { useAuthUser, useSignIn } from "hooks/authentication";
import { UnexpectedError } from "pages/error/unexpectedError/UnexpectedError";
import { useLocalStorage } from "usehooks-ts";
import { FeedbackButton } from "./components/feedback/FeedbackButton";
import { FeedbackDialog } from "./components/feedback/FeedbackDialog";
import { FullScreenLoader } from "./components/loader/FullScreenLoader";
import { MaintenanceBanner } from "./components/maintenance/MaintenanceBanner";
import { useAppConfig } from "./config/app-config";
import { useDatadogTracking } from "./hooks/useDatadogTracking";
import { useExternalViewSync } from "./hooks/useExternalViewSync";
import { useHierarchyData } from "./hooks/useHierarchyData";
import { usePostAuthNavigation } from "./hooks/usePostAuthNavigation";
import { AppThemeProvider } from "./providers/AppThemeProvider";
import { DatasetProvider } from "./providers/DatasetProvider";
import {
  defaultSettings,
  SettingsProvider,
} from "./providers/SettingsProvider";

export interface AppProps {
  signInIdentityProvider: string;
}

export const App = ({ signInIdentityProvider }: AppProps) => {
  const { state } = useSignIn(signInIdentityProvider);
  const { user } = useAuthUser();
  const [appConfig, appConfigLoading, setExternalViewVisible] = useAppConfig();
  const [settings] = useLocalStorage("search-settings", defaultSettings);

  // Fetch hierarchy data based on current settings
  const hierarchyData = useHierarchyData(settings.externalViewVisible);

  // Apply global side effects
  usePostAuthNavigation(state);
  useDatadogTracking(user, settings);
  useExternalViewSync(settings, setExternalViewVisible);

  if (appConfigLoading) {
    return <FullScreenLoader />;
  }

  return (
    <AppThemeProvider
      primaryColor={appConfig.ux.theme.color.primary}
      secondaryColor={appConfig.ux.theme.color.secondary}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <SettingsProvider hierachyData={hierarchyData}>
          <DatasetProvider user={user}>
            <CssBaseline />
            <CovFeatureGate flag="maintenanceBanner.enabled">
              <MaintenanceBanner />
            </CovFeatureGate>
            <CdcScriptStatus />
            <ErrorBoundary
              fallback={(error) => <UnexpectedError error={error} />}
            >
              <AppRouting />
              {appConfig.ux.feedback?.floatingActionButton.enabled && (
                <FeedbackButton
                  targetUrl={
                    appConfig.ux.feedback.floatingActionButton.redirectUrl
                  }
                />
              )}
              {appConfig.ux.feedback?.dialog.enabled && (
                <FeedbackDialog
                  targetUrl={appConfig.ux.feedback.dialog.redirectUrl}
                />
              )}
            </ErrorBoundary>
          </DatasetProvider>
        </SettingsProvider>
      </LocalizationProvider>
    </AppThemeProvider>
  );
};

export default App;
